// KPIs › Priorities in Founder OS (FOUNDER-OS phase 1; was Portfolio › Goals; 12 "Portfolio
// priorities … cascade into company priorities"). "Edit priorities" opens the ordinary
// priorities board of the portfolio company (`?board=own`), where they are set:
// the portfolio's priorities of the quarter, each with its roll-up progress (the mean of its
// linked company priorities) and the company priorities it cascades into. "Link a company
// priority" picks a company, then one of its priorities (read through that company's own
// Priorities route), and links it; each link has a visible unlink control. Owner only for both.
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Progress,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { LinkIcon, TargetIcon, UnlinkIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CascadeLink, PortfolioGoal } from "../../api/types";
import { SwitcherTile } from "../../shell/company-switcher";
import type { CompanyAccess } from "../company";
import { prioritiesQuery } from "../priorities/priorities-screen";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { pct } from "./portfolio-parts";
import {
  focusQuery,
  portfolioGoalsQuery,
  portfolioKeys,
  useCanEditPortfolio,
} from "./portfolio-queries";

const TRACK: Record<
  PortfolioGoal["track"],
  { label: string; tone: "success" | "warning" | "danger" }
> = {
  on: { label: "On track", tone: "success" },
  at_risk: { label: "At risk", tone: "warning" },
  off: { label: "Off track", tone: "danger" },
};

const STATE_LABEL: Partial<Record<PortfolioGoal["state"], string>> = {
  achieved: "Achieved",
  missed: "Missed",
  dropped: "Dropped",
};

function Bar({ value, label }: { value: number | null; label: string }) {
  return (
    <span className="flex items-center gap-2" data-slot="goal-progress">
      <Progress
        value={value === null ? 0 : Math.round(value * 100)}
        aria-label={label}
        className="w-28"
      />
      <span className="text-xs text-muted-foreground tabular-nums">
        {value === null ? "No links yet" : pct(value)}
      </span>
    </span>
  );
}

export function PortfolioGoalsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const goals = useQuery(portfolioGoalsQuery(api, company.id));
  // An owner of the whole account cascades (a grant limited to some companies reads only).
  const owner = useCanEditPortfolio(api, company.id);
  const qc = useQueryClient();
  const [linking, setLinking] = useState<PortfolioGoal | null>(null);
  const unlink = useMutation({
    mutationFn: (l: CascadeLink) =>
      api.client.portfolio.goalLinks.delete({ params: { co: company.id, id: l.link_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: portfolioKeys.root(company.id) }),
  });
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-goals">
      <LocationHeader
        icon={TargetIcon}
        crumbs={[
          {
            label: "KPIs",
            render: <Link to="/c/$slug/kpis" params={{ slug: company.slug }} />,
          },
        ]}
        title="Priorities"
        badge={goals.data ? <Badge variant="outline">{goals.data.quarter}</Badge> : null}
        actions={
          <Link
            to="/c/$slug/kpis/priorities"
            params={{ slug: company.slug }}
            search={{ board: "own" } as never}
            className="text-sm font-medium text-primary-text hover:underline"
          >
            Edit priorities
          </Link>
        }
      />
      <div className="flex min-w-0 flex-col gap-4 px-6 py-5 max-md:px-4">
        {goals.isPending ? (
          <Loading label="Loading the portfolio priorities" />
        ) : goals.isError ? (
          <LoadError what="The portfolio priorities" error={goals.error} />
        ) : goals.data.goals.length === 0 ? (
          <Nothing title="No portfolio priorities this quarter">
            Set them with Edit priorities, then cascade them into the companies here.
          </Nothing>
        ) : (
          goals.data.goals.map((g) => (
            <article
              key={g.goal_id}
              aria-label={g.title}
              data-slot="portfolio-goal"
              className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-xs"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {g.state === "open" ? (
                  <Badge variant={TRACK[g.track].tone}>{TRACK[g.track].label}</Badge>
                ) : (
                  <Badge variant="neutral" data-state={g.state}>
                    {STATE_LABEL[g.state] ?? g.state}
                  </Badge>
                )}
                <h2 className="min-w-0 flex-1 font-heading text-[15px] font-semibold">{g.title}</h2>
                <Bar value={g.progress} label={`${g.title}: progress`} />
                {owner ? (
                  <Button size="sm" variant="outline" onClick={() => setLinking(g)}>
                    <LinkIcon aria-hidden="true" />
                    Link a company priority
                  </Button>
                ) : null}
              </div>
              {g.links.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Not cascaded yet: link the company priorities that deliver it.
                </p>
              ) : (
                <ul
                  className="flex flex-col divide-y divide-border-subtle rounded-md border border-border"
                  aria-label={`${g.title}: company priorities`}
                >
                  {g.links.map((l) => (
                    <li
                      key={l.link_id}
                      data-slot="cascade-link"
                      className="group/row flex items-center gap-2.5 px-3 py-2 text-sm"
                    >
                      <SwitcherTile name={l.company_name} portfolio={false} />
                      <span className="w-32 shrink-0 truncate text-xs font-medium text-secondary max-sm:w-20">
                        {l.company_name}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {l.title ?? "Not in the projection yet"}
                      </span>
                      {l.track ? (
                        <Badge variant={l.state !== "open" ? "neutral" : TRACK[l.track].tone}>
                          {l.state !== "open" ? l.state : TRACK[l.track].label}
                        </Badge>
                      ) : null}
                      <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">
                        {l.progress === null ? "—" : pct(l.progress)}
                      </span>
                      {owner ? (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Unlink ${l.title ?? "this priority"} (${l.company_name})`}
                          title="Unlink"
                          disabled={unlink.isPending}
                          onClick={() => unlink.mutate(l)}
                        >
                          <UnlinkIcon aria-hidden="true" />
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))
        )}
      </div>
      {linking ? (
        <LinkDialog api={api} co={company.id} goal={linking} onClose={() => setLinking(null)} />
      ) : null}
    </div>
  );
}

function LinkDialog({
  api,
  co,
  goal,
  onClose,
}: {
  api: Api;
  co: string;
  goal: PortfolioGoal;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const focus = useQuery(focusQuery(api, co));
  const companies = focus.data?.companies ?? [];
  const [companyId, setCompanyId] = useState<string>("");
  const [goalId, setGoalId] = useState<string>("");
  const chosen = companyId || companies[0]?.company_id || "";
  const board = useQuery({ ...prioritiesQuery(api, chosen, undefined), enabled: !!chosen });
  const linked = new Set(goal.links.map((l) => `${l.company_id}:${l.goal_id}`));
  const options = (board.data?.data ?? []).filter((g) => !linked.has(`${chosen}:${g.id}`));
  const link = useMutation({
    mutationFn: () =>
      api.client.portfolio.goalLinks.create({
        params: { co },
        body: { portfolio_goal_id: goal.goal_id, company_id: chosen, company_goal_id: goalId },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: portfolioKeys.root(co) });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link a company priority</DialogTitle>
          <DialogDescription>
            “{goal.title}” rolls up the progress of the company priorities linked to it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-company`}>Company</Label>
          <NativeSelect
            id={`${id}-company`}
            value={chosen}
            className="w-full"
            onChange={(e) => {
              setCompanyId(e.target.value);
              setGoalId("");
            }}
          >
            {companies.map((c) => (
              <NativeSelectOption key={c.company_id} value={c.company_id}>
                {c.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium">Priority</legend>
          {board.isPending ? (
            <Loading label="Loading the company's priorities" rows={2} />
          ) : board.isError ? (
            <LoadError what="The company's priorities" error={board.error} />
          ) : options.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No priority left to link this quarter in that company.
            </p>
          ) : (
            <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {options.map((g) => (
                <li key={g.id}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm",
                      goalId === g.id ? "border-primary bg-primary-soft" : "border-border",
                    )}
                  >
                    <input
                      type="radio"
                      name={`${id}-goal`}
                      value={g.id}
                      checked={goalId === g.id}
                      onChange={() => setGoalId(g.id)}
                      className="accent-(--primary)"
                    />
                    <span className="min-w-0 flex-1 truncate">{g.title}</span>
                    {g.state === "open" ? (
                      <Badge variant={TRACK[g.track].tone}>{TRACK[g.track].label}</Badge>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!goalId || link.isPending} onClick={() => link.mutate()}>
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
